import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { Invoice } from 'entities';

import { Collapse, DatePicker, Input, Row, LabeledSelect } from 'components/Blocks';

import { FormControlHook } from 'hooks';

import { omitUndefined } from 'utils/object';
import { getDateToLocaleStartOfDay } from 'utils/date';

import { InvoiceCreationProps } from './types';

interface InvoiceDateProps
  extends Pick<FormControlHook<InvoiceCreationProps>, 'state' | 'dispatch'> {
  children?: never;
}

const optionConfig: Record<
  Invoice['dateRequest']['type'],
  {
    value: string;
    getLabel: (t: TFunction) => string;
    valueType: 'input' | 'date' | null;
    defaultValue?: string;
  }
> = {
  NOT_SPECIFIED: {
    getLabel: (t) => t('not-specified'),

    value: 'NOT_SPECIFIED',

    valueType: null,
  },

  EXACT: {
    getLabel: (t) => t('exact'),

    value: 'EXACT',

    valueType: 'date',

    defaultValue: getDateToLocaleStartOfDay(),
  },

  RANGE: {
    getLabel: (t) => t('payment-term'),

    value: 'RANGE',
    valueType: 'input',

    defaultValue: '',
  },
};

export function InvoiceDate({ state, dispatch }: InvoiceDateProps): JSX.Element {
  const { t } = useLang();

  const handleTypeChange = (type: string): void => {
    const { defaultValue } = optionConfig[type as Invoice['dateRequest']['type']];

    dispatch({
      dateRequest: ({ dateRequest }) =>
        dateRequest.type !== type
          ? (omitUndefined({
              type,
              value: defaultValue,
            }) as Invoice['dateRequest'])
          : dateRequest,
    });
  };

  const { type, value } = state.dateRequest;

  const handleDateChange = (date: Date | undefined): void => {
    if (!date) return;

    dispatch({
      dateRequest: omitUndefined({
        type,
        value: getDateToLocaleStartOfDay(date),
      }) as Invoice['dateRequest'],
    });
  };

  const handleTextChange = ({ target }: { target: { value: string } }): void => {
    dispatch({
      dateRequest: omitUndefined({
        type,
        value: target.value,
      }) as Invoice['dateRequest'],
    });
  };

  return (
    <>
      <LabeledSelect
        id="dateRequest"
        label={t('date-reference') as string}
        value={type}
        onChangeDirect={handleTypeChange}
        options={Object.values(optionConfig).map(({ getLabel, value }) => ({
          value,
          label: getLabel(t),
        }))}
      />

      <Collapse expanded={state.dateRequest.type !== 'NOT_SPECIFIED'}>
        <Row justifyContent="center">
          {type === 'EXACT' ? (
            <DatePicker
              disablePast
              selected={value ? new Date(value) : new Date()}
              onSelect={handleDateChange}
              className="w-full"
            />
          ) : (
            <Input
              value={value}
              onChange={handleTextChange}
              placeholder={t('30-days-60-days-no-date') as string}
            />
          )}
        </Row>
      </Collapse>
    </>
  );
}
